import * as React from 'react';
import { useEffect } from 'react';
import { X } from 'lucide-react';
import { TermsContent } from './TermsContent';
import { PrivacyContent } from './PrivacyContent';

interface LegalModalProps {
  view: 'terms' | 'privacy' | null;
  onClose: () => void;
}

export const LegalModal: React.FC<LegalModalProps> = ({ view, onClose }) => {
  useEffect(() => {
    if (!view) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [view, onClose]);

  // Prevent body scroll while modal is open
  useEffect(() => {
    if (view) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [view]);

  if (!view) return null;

  const title = view === 'terms' ? 'Terms of Service' : 'Privacy Policy';

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-modal-title"
    >
      {/* Tap-outside to close */}
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />

      {/* Modal card */}
      <div className="relative z-10 w-full max-w-2xl bg-paper rounded-lg shadow-overlay flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-ink-200 shrink-0">
          <h2
            id="legal-modal-title"
            className="font-serif font-medium text-lg text-ink"
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-ink-500 hover:text-ink transition-colors p-1 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-8 py-6 flex-1 text-sm text-ink-600 leading-relaxed space-y-6">
          {view === 'terms' ? <TermsContent /> : <PrivacyContent />}
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-ink-200 shrink-0 flex justify-end">
          <button
            onClick={onClose}
            className="text-[10px] font-medium uppercase tracking-widest px-5 py-2.5 bg-oxblood text-paper rounded-md hover:bg-oxblood/90 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
